import { hasAnyPermission } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { SlotExtendedClient } from '@frontend/slot/api';
import { slotRequest } from '@frontend/slot/events';
import { Slot, SlotExtended } from '@frontend/slot/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';
import {
    SlotActivityLaundrySection,
    SlotActivityLendingSection,
    SlotActivityPudoSection,
    SlotActivityVendingSection,
    SlotConstraintsSection,
    SlotContentsSection,
    SlotDetailsCard,
    SlotDimensionsSection,
    SlotIdentificationSection
} from './slot-detail-sections';

function canReadIdentifications(view: ReactiveDetailView<Slot, SlotExtended>): boolean {
    return (
        view.permissions != null &&
        hasAnyPermission(view.permissions, [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: view.object.account_id }])
    );
}

export const slotReactiveDetail: ReactiveDetailRegistryEntry<Slot, SlotExtended> = {
    entityType: EntityType.SLOT,
    resolveFunction: slotRequest,
    fetchExtended: ({ object, parents }) =>
        SlotExtendedClient.fetchSlotExtended(object.account_id, parents.spot_id ?? object.spot_id, parents.module_id ?? object.module_id, object.id),
    title: ({ extended, object }) => (
        <>
            {CommonMessage.OBJECTS.SLOT.DEFAULT}: {extended?.display ?? object.display ?? object.number ?? object.index}
        </>
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => {
                const slot = view.extended;
                if (!slot) return null;
                return (
                    <>
                        <SlotDetailsCard slot={slot} />
                        <SlotDimensionsSection slot={slot} />
                    </>
                );
            }
        },
        {
            id: 'identification',
            label: CommonMessage.OBJECTS.IDENTIFICATION.PLURAL,
            visible: canReadIdentifications,
            render: (view) => (view.extended ? <SlotIdentificationSection slot={view.extended} /> : null)
        },
        {
            id: 'activity',
            label: (
                <FormattedMessage
                    id='platform.slot.modal.slot-detail-modal.activity-tab'
                    description='Tab label for SLOT activity (transactions) on the SLOT detail pane'
                    defaultMessage='Activity'
                />
            ),
            dropdownOptions: [
                {
                    id: 'pudo',
                    label: getApplicationDisplayName(Application.PUDO),
                    render: (view) => (view.extended ? <SlotActivityPudoSection slot={view.extended} /> : null)
                },
                {
                    id: 'laundry',
                    label: getApplicationDisplayName(Application.LAUNDRY),
                    render: (view) => (view.extended ? <SlotActivityLaundrySection slot={view.extended} /> : null)
                },
                {
                    id: 'vending',
                    label: getApplicationDisplayName(Application.VENDING),
                    render: (view) => (view.extended ? <SlotActivityVendingSection slot={view.extended} /> : null)
                },
                {
                    id: 'lending',
                    label: getApplicationDisplayName(Application.LENDING),
                    render: (view) => (view.extended ? <SlotActivityLendingSection slot={view.extended} /> : null)
                }
            ]
        },
        {
            id: 'contents',
            label: (
                <FormattedMessage
                    id='spots.contents.title'
                    defaultMessage='Contents'
                />
            ),
            render: (view) => (view.extended ? <SlotContentsSection slot={view.extended} /> : null)
        },
        {
            id: 'constraints',
            label: CommonMessage.OBJECTS.CONSTRAINT.PLURAL,
            render: (view) => (view.extended ? <SlotConstraintsSection slot={view.extended} /> : null)
        }
    ]
};
